import { Button } from "@xola/ui-kit";
import { getPartnerFee, isGift, isMistPurchase, PurchaseActionOption } from "common";
import React, { useState } from "react";
import { useQueryClient } from "react-query";
import { Permission, Purchase, PurchaseItem } from "types";
import { useAuthorization } from "../../../hooks/useAuthorization";
import { useInvalidate } from "../../../hooks/useInvalidation";
import { areAllItemsPending, getPendingItemsAmount, hasBalance } from "../../../models/Purchase";
import {
    CollectBalanceOption,
    CollectBalanceOptionsModal,
} from "../../PurchaseModification/CollectBalanceDeciderModal";
import { CollectPurchaseBalanceViaInvoiceModal } from "../../PurchaseModification/CollectBalanceViaInvoiceModal";
import { CollectPurchaseBalanceModal } from "../../PurchaseModification/CollectPurchaseBalanceModal";

interface CollectBalanceButtonProps {
    purchase: Purchase;
    title?: string;
    size?: string;
    icon?: any;
    item?: PurchaseItem; // Item will be undefined if you're at the root level of a purchase
}

export const CollectBalanceButton = ({ purchase, item, title, icon, size }: CollectBalanceButtonProps) => {
    const invalidate = useInvalidate();
    const queryClient = useQueryClient();
    const { isGranted } = useAuthorization();
    const [currentAction, setCurrentAction] = useState<PurchaseActionOption | null>(null);

    const handleCollectBalance = () => {
        setCurrentAction(PurchaseActionOption.COLLECT_BALANCE_OPTIONS);
    };

    const handleApplyAction = () => {
        invalidate("purchases", purchase.id);
        queryClient.invalidateQueries("purchaseNotifications");
        setCurrentAction(null);
    };

    const purchaseItem = item ?? purchase.items[0]; // Introduced this var instead of reassigning to item, since we need item-undefined logic for Mist element

    const showBalanceButton = isGranted(Permission.MODIFY_BOOKING_LITE) && canShowCollectBalanceButton(purchase, item);

    if (!showBalanceButton) {
        return null;
    }

    const isCollectPurchaseBalanceModalOpen = currentAction === PurchaseActionOption.COLLECT_BALANCE;
    const isCollectPurchaseBalanceViaInvoiceModalOpen =
        currentAction === PurchaseActionOption.COLLECT_BALANCE_VIA_INVOICE;
    const isCollectBalanceOptionsModalOpen = currentAction === PurchaseActionOption.COLLECT_BALANCE_OPTIONS;

    return (
        <>
            <Button size={size} color="success" onClick={handleCollectBalance}>
                <div className="flex space-x-2">
                    <span>{icon ? icon : null}</span>
                    <span>{title ?? "Collect Balance"}</span>
                </div>
            </Button>

            {isCollectPurchaseBalanceModalOpen && (
                <CollectPurchaseBalanceModal
                    purchase={purchase}
                    isOpen={isCollectPurchaseBalanceModalOpen}
                    onClose={handleApplyAction}
                />
            )}

            {isCollectBalanceOptionsModalOpen && (
                <CollectBalanceOptionsModal
                    isOpen={isCollectBalanceOptionsModalOpen}
                    purchase={purchase}
                    onClose={() => setCurrentAction(null)}
                    onConfirm={(action: CollectBalanceOption) => {
                        if (action === CollectBalanceOption.COLLECT_NOW) {
                            setCurrentAction(PurchaseActionOption.COLLECT_BALANCE);
                        } else if (action === CollectBalanceOption.COLLECT_VIA_INVOICE) {
                            setCurrentAction(PurchaseActionOption.COLLECT_BALANCE_VIA_INVOICE);
                        } else {
                            setCurrentAction(null);
                        }
                    }}
                />
            )}

            {isCollectPurchaseBalanceViaInvoiceModalOpen && (
                <CollectPurchaseBalanceViaInvoiceModal
                    selectClosestArrivalDate
                    purchase={purchase}
                    item={purchaseItem}
                    isOpen={isCollectPurchaseBalanceViaInvoiceModalOpen}
                    onClose={() => setCurrentAction(null)}
                />
            )}
        </>
    );
};

const canShowCollectBalanceButton = (purchase: Purchase, item?: PurchaseItem) => {
    let showBalanceButton = hasBalance(purchase) && purchase.balance > 0;

    const isMist = isMistPurchase(purchase);
    const isItemSelected = !!item; // Tells us if component is being rendered at purchase or item level
    if (isMist && isItemSelected) {
        // We are at the item level of a mist purchase, we do not show collect balance button here
        showBalanceButton = false;
    }

    const partnerFeeLineItem = getPartnerFee(purchase);
    const partnerFee = partnerFeeLineItem?.includeInAmount === true ? partnerFeeLineItem.amount : 0;
    const balance = partnerFee > 0 ? purchase.balance - partnerFee : purchase.balance;

    // If all items are pending and balance is equal to total pending amount then don't show Collect balance button
    if (areAllItemsPending(purchase) && getPendingItemsAmount(purchase) === balance) {
        showBalanceButton = false;
    }

    if (item && isGift(item)) {
        showBalanceButton = false;
    }

    return showBalanceButton;
};
